import { responseHandler } from "@msuite/ui/utils";
import type { ResourceLinkFormSchema } from "@msuite/utils/schemas";
import { useState } from "react";
import { client } from "@/hono";

/**
 * A link staged on a ticket that does not exist yet. It carries what the form collected minus the
 * scope, because the ticket it belongs to is only known once the ticket has been created.
 */
export type PendingResourceLink = Pick<ResourceLinkFormSchema, "link" | "name">;

export type ResourceLinkController = ReturnType<typeof useResourceLinkController>;

export function useResourceLinkController() {
	/** State */
	const [pendingLinks, setPendingLinks] = useState<PendingResourceLink[]>([]);

	/** Functions */
	function handleOnAddPendingLink(pendingLink: PendingResourceLink) {
		setPendingLinks((links) => [...links, pendingLink]);
	}

	function handleOnRemovePendingLink(link: string) {
		setPendingLinks((links) => links.filter((pendingLink) => pendingLink.link !== link));
	}

	function handleOnReset() {
		setPendingLinks([]);
	}

	/**
	 * Persist all locally staged links once the ticket they belong to has been created.
	 *
	 * @param projectId - The project the newly created ticket belongs to.
	 * @param ticketId - The id of the ticket that was just created.
	 */
	async function handleOnPersistPendingLinks(projectId: string, ticketId: string) {
		if (pendingLinks.length < 1) return;
		await Promise.all(
			pendingLinks.map((pendingLink) =>
				client.projects[":projectId"]["resource-links"].$post({
					param: { projectId },
					json: { ...pendingLink, ticketId },
				}),
			),
		).then((responses) => Promise.all(responses.map((response) => responseHandler(response))));
		handleOnReset();
	}

	/** Return */
	return {
		pendingLinks,
		handleOnAddPendingLink,
		handleOnRemovePendingLink,
		handleOnReset,
		handleOnPersistPendingLinks,
	};
}
